import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBrand } from "@/components/BrandText";

const faqs = [
  {
    q: "What is One More Shift?",
    a: "One More Shift is an experience and advisory network being built in the communities south and southeast of Houston. It connects businesses and individuals with experienced people — we call them One More Shift Advisors — including retired, semi-retired and late-career professionals, for defined short-term needs like consulting, mentoring, training, assessments, troubleshooting and project work.",
    testId: "faq-what-is",
  },
  {
    q: "Is this a staffing agency?",
    a: "No. One More Shift doesn't fill positions. We help solve defined problems using experienced advisors. One More Shift is being developed as an experience/advisory network — our advisors help with defined consulting, mentoring, training and project needs rather than filling conventional full-time positions.",
    testId: "faq-staffing",
  },
  {
    q: "Do I have to be retired?",
    a: "No. One More Shift Advisors are welcome at any stage — retired, semi-retired, late-career or simply experienced. The network is centered around meaningful practical experience, not a specific age or retirement status.",
    testId: "faq-retired",
  },
  {
    q: "Do I need a college degree?",
    a: "Not necessarily. One More Shift Advisors may need relevant credentials for certain regulated work, but we also recognize expertise gained through years of practical experience.",
    testId: "faq-degree",
  },
  {
    q: "Will I definitely get engagements if I submit my information?",
    a: "No. Becoming a One More Shift Advisor does not guarantee engagements. We're building the founding network and will review your experience to see where it might fit.",
    testId: "faq-guarantee",
  },
  {
    q: "Which areas does One More Shift serve?",
    a: "We're starting local — initially serving Pearland, Friendswood, Alvin, League City, Webster, Dickinson and the Clear Lake area. We're intentionally beginning with a small group of nearby communities so we can personally build and test the founding network.",
    testId: "faq-outside-houston",
  },
  {
    q: "Can businesses request a specific kind of experience?",
    a: "Yes. Describe the problem even if you don't know exactly what type of advisor you need — that's exactly what we're here to help figure out.",
    testId: "faq-specific-experience",
  },
  {
    q: "What about licenses, certifications and background checks?",
    a: "Certain services may require licenses, certifications, background checks or other qualifications, and availability will depend on the engagement. One More Shift Advisors do not perform work that requires a professional license unless properly credentialed, and One More Shift does not provide medical, psychological or professional child-development advice.",
    testId: "faq-licenses",
  },
];

export default function Faq() {
  usePageTitle(
    "FAQ | One More Shift",
    "Frequently asked questions about One More Shift, the experience and advisory network starting local in the communities south and southeast of Houston."
  );
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="max-w-4xl mx-auto px-6 py-20" data-testid="faq-page">
      <h1 className="text-4xl md:text-5xl font-bold" data-testid="faq-headline">
        Frequently Asked Questions
      </h1>
      <div className="mt-12 space-y-4">
        {faqs.map((f, i) => {
          const open = openIndex === i;
          return (
            <div key={f.q} className="bg-card border border-border rounded-lg" data-testid={f.testId}>
              <button
                data-testid={`${f.testId}-toggle`}
                className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 text-xl font-semibold text-primary"
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? null : i)}
              >
                {f.q}
                <ChevronDown
                  size={28}
                  className={`shrink-0 text-secondary transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {open && (
                <p className="px-6 pb-6 text-lg text-foreground leading-relaxed" data-testid={`${f.testId}-answer`}>
                  {withBrand(f.a)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
